import React from 'react'
import { SectionCard } from './SectionCard'
import { VIDEO_ORDER, VIDEO_LABELS, normalizeVideoMetrics } from './videoMetrics'
import type { DashboardOverview } from '../../../types/dashboard'

const MAIN_COLORS = [
  'bg-brand-teal',       // Impressions      — deep teal
  'bg-brand-cyan',       // Clicks           — medium cyan
  'bg-primary-600',      // CTR %            — bright green
  'bg-brand-turquoise',  // Engagement       — grey turquoise
  'bg-brand-pearl',      // Engagement Rate  — near-black
]

const VIDEO_COLORS = [
  'bg-primary-700',      // 5s View          — dark green
  'bg-primary-800',      // 25% View         — forest green
  'bg-primary-900',      // 50% View         — very dark green
  'bg-primary-950',      // 75% View         — darkest green
  'bg-brand-dark',       // 100% View        — charcoal
]

interface StatCardProps {
  label: string
  value: string
  colorClass: string
}
function StatCard({ label, value, colorClass }: StatCardProps) {
  return (
    <div className={`${colorClass} rounded-lg p-4 flex flex-col gap-1`}>
      <span className="text-2xl font-bold text-white">{value}</span>
      <span className="text-sm text-white/70">{label}</span>
    </div>
  )
}

interface Props {
  overview: DashboardOverview
  sectionRef?: React.RefObject<HTMLDivElement>
}

export function OverviewSection({ overview, sectionRef }: Props) {
  const normalizedMetrics = normalizeVideoMetrics(overview.video_metrics)
  const videoEntries = VIDEO_ORDER.filter(k => normalizedMetrics[k] !== undefined && normalizedMetrics[k].count > 0)

  const mainStats = [
    { label: 'Impressions',      value: overview.impressions.toLocaleString() },
    { label: 'Clicks',           value: overview.clicks.toLocaleString() },
    { label: 'CTR %',            value: `${overview.ctr.toFixed(2)}%` },
    { label: 'Engagement',       value: overview.engagement.toLocaleString() },
    { label: 'Engagement Rate %',value: `${overview.engagement_rate.toFixed(2)}%` },
  ]

  return (
    <SectionCard title="Overview" sectionRef={sectionRef} id="section-overview">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {mainStats.map((s, i) => (
          <StatCard key={s.label} label={s.label} value={s.value} colorClass={MAIN_COLORS[i]} />
        ))}
      </div>

      {videoEntries.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          {videoEntries.map((key, i) => {
            const m = normalizedMetrics[key]
            return (
              <StatCard
                key={key}
                label={VIDEO_LABELS[key] || key}
                value={`${m.count.toLocaleString()} (${m.pct.toFixed(2)}%)`}
                colorClass={VIDEO_COLORS[i % VIDEO_COLORS.length]}
              />
            )
          })}
        </div>
      )}
    </SectionCard>
  )
}
